import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { datadogRum } from "@datadog/browser-rum";

import { EndorsementEligibilityBlockReason } from "@covertree/agent-portal-graphql";
import { Toast } from "@covertree/utils";

import { useAppDispatch } from "@redux/hooks";
import { checkEndorsementEligibility } from "@redux/slices/endorsement";
import { EndorsementRoutes, navigateToEndorsementStep } from "@routing/paths";

export type EndorsementBlockingReason =
  | EndorsementEligibilityBlockReason
  | "Unknown";

export const useOpenNewEndorsement = (policyLocator: string | undefined) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [isEligibilityLoading, setIsEligibilityLoading] = useState(false);
  const [blockingReason, setBlockingReason] =
    useState<EndorsementBlockingReason | null>(null);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const openNewEndorsement = useCallback(
    async ({ fromAdditionalInterestTab = false } = {}) => {
      try {
        setIsEligibilityLoading(true);
        const result = await dispatch(
          checkEndorsementEligibility({
            policyLocator: policyLocator ?? "",
          })
        ).unwrap();

        if (!isMountedRef.current) return;

        if (!result.isEligible) {
          setBlockingReason(result.reason ?? "Unknown");
          return;
        }

        await navigateToEndorsementStep(
          navigate,
          policyLocator ?? "",
          EndorsementRoutes.INSURED_INFO,
          {
            dispatch,
            mode: "create",
            tab: fromAdditionalInterestTab ? "endorsements" : undefined,
            state: fromAdditionalInterestTab
              ? { expandAdditionalInterest: true }
              : undefined,
          }
        );
      } catch (e) {
        if (!isMountedRef.current) return;
        datadogRum.addError("Failed to check endorsement eligibility", { e });
        Toast.showError(
          "Failed to check endorsement eligibility. Please try again later."
        );
      } finally {
        if (isMountedRef.current) setIsEligibilityLoading(false);
      }
    },
    [dispatch, navigate, policyLocator]
  );

  return {
    openNewEndorsement,
    isEligibilityLoading,
    blockingReason,
    setBlockingReason,
  };
};
